import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { goToHomeSection } from '../lib/hashSectionNav';
import { SITE_IMAGES } from '../constants/publicAssets';
import { SPRINTFIT_COLLECTIONS_URL } from '../constants/storeUrls';

const NAV_LINKS = [
  { label: 'Science', to: '/science' },
  { label: 'Films', href: '#films' },
  // { label: 'Finder', href: '#body-map' }, // re-enable with BodyMapSection on Home
  { label: 'Shop', href: '#product-rail' },
  { label: 'Guide', href: '#recovery-guide' },
  { label: 'Reviews', href: '#reviews' },
  { label: 'FAQ', href: '#faq' },
];

function homeHeroPastThreshold() {
  return window.scrollY >= Math.min(window.innerHeight * 0.55, 640);
}

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [pastHero, setPastHero] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => {
      setIsScrolled(window.scrollY > 20);
      if (location.pathname === '/') setPastHero(homeHeroPastThreshold());
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [location.pathname]);

  useEffect(() => {
    setIsMobileOpen(false);
  }, [location]);

  const handleHashClick = (e, href) => {
    e.preventDefault();
    const id = href.replace('#', '');
    setIsMobileOpen(false);
    goToHomeSection(navigate, location.pathname, id);
  };

  const homeOnVideo = isHome && !pastHero;

  const navBar = isHome
    ? homeOnVideo
      ? isScrolled
        ? 'bg-black/55 backdrop-blur-xl border-b border-white/[0.08] shadow-[0_8px_32px_-12px_rgba(0,0,0,0.5)]'
        : 'bg-black/40 backdrop-blur-xl border-b border-white/[0.06]'
      : isScrolled
        ? 'bg-[#f3f1ec]/95 backdrop-blur-xl border-b border-stone-200/90 shadow-[0_12px_40px_-16px_rgba(28,27,25,0.12)]'
        : 'bg-[#f3f1ec]/82 backdrop-blur-xl border-b border-stone-200/70'
    : isScrolled
      ? 'bg-[#090a0b]/92 backdrop-blur-xl border-b border-white/[0.07] shadow-[0_8px_32px_-12px_rgba(0,0,0,0.65)]'
      : 'bg-[#090a0b]/75 backdrop-blur-xl border-b border-white/[0.05]';

  const linkClass = homeOnVideo
    ? 'font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400 hover:text-white transition-colors duration-300 cursor-pointer'
    : isHome
      ? 'font-mono text-[11px] uppercase tracking-[0.2em] text-stone-600 hover:text-[#1c1b19] transition-colors duration-300 cursor-pointer'
      : 'font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500 hover:text-white transition-colors duration-300 cursor-pointer';

  const exploreClass = homeOnVideo
    ? 'font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-300 hover:text-white transition-colors duration-300 border border-white/20 hover:border-white/35 px-3 py-1.5 shrink-0'
    : isHome
      ? 'font-mono text-[11px] uppercase tracking-[0.18em] text-stone-700 hover:text-[#1c1b19] transition-colors duration-300 border border-stone-300/90 hover:border-[#2c5f54]/40 px-3 py-1.5 shrink-0'
      : 'font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-300 hover:text-white transition-colors duration-300 border border-white/15 hover:border-white/30 px-3 py-1.5 shrink-0';

  const logoMark = homeOnVideo
    ? 'ring-1 ring-white/15 bg-zinc-950/80 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)]'
    : isHome
      ? 'ring-1 ring-stone-200/90 bg-white shadow-sm'
      : 'ring-1 ring-white/10 bg-zinc-950/90 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)]';

  const wordmark = homeOnVideo || !isHome ? 'text-white' : 'text-stone-900';
  const cubeAccent = homeOnVideo || !isHome ? 'text-[#8fb8ae]' : 'text-[#2c5f54]';
  const subLabel = homeOnVideo
    ? 'text-zinc-500 group-hover:text-zinc-400'
    : isHome
      ? 'text-stone-500 group-hover:text-stone-700'
      : 'text-zinc-500 group-hover:text-zinc-400';

  const buyBtn = homeOnVideo
    ? 'hidden md:inline-flex items-center h-10 px-7 bg-white text-neutral-900 font-heading text-xs font-semibold uppercase tracking-[0.22em] hover:bg-[#c5ebe2] transition-colors duration-300 border border-white/10 shadow-sm'
    : isHome
      ? 'hidden md:inline-flex items-center h-10 px-7 bg-[#1c1b19] text-[#f5f3ef] font-heading text-xs font-semibold uppercase tracking-[0.22em] hover:bg-[#2c5f54] transition-colors duration-300 border border-[#1c1b19] shadow-md'
      : 'hidden md:inline-flex items-center h-10 px-7 bg-white text-neutral-900 font-heading text-xs font-semibold uppercase tracking-[0.22em] hover:bg-zinc-200 transition-colors duration-300 border border-white/10 shadow-sm';

  const menuIcon = homeOnVideo || !isHome ? 'text-white hover:text-zinc-300' : 'text-stone-800 hover:text-stone-600';

  const mobileExplore = homeOnVideo
    ? 'md:hidden font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white transition-colors px-1 py-2 max-w-[5.5rem] leading-tight text-center'
    : isHome
      ? 'md:hidden font-mono text-[10px] uppercase tracking-widest text-stone-600 hover:text-stone-900 transition-colors px-1 py-2 max-w-[5.5rem] leading-tight text-center'
      : 'md:hidden font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white transition-colors px-1 py-2 max-w-[5.5rem] leading-tight text-center';

  const mobilePanel = homeOnVideo
    ? 'md:hidden bg-[#0c0c0d]/98 border-t border-white/[0.06] backdrop-blur-xl'
    : isHome
      ? 'md:hidden bg-[#faf8f5]/98 border-t border-stone-200/90 backdrop-blur-xl'
      : 'md:hidden bg-[#0c0c0d]/98 border-t border-white/[0.06] backdrop-blur-xl';

  const mobileLink = homeOnVideo
    ? 'block py-3 font-mono text-sm uppercase tracking-[0.16em] text-zinc-500 hover:text-white border-b border-white/[0.04] transition-colors'
    : isHome
      ? 'block py-3 font-mono text-sm uppercase tracking-[0.16em] text-stone-600 hover:text-stone-950 border-b border-stone-200/80 transition-colors'
      : 'block py-3 font-mono text-sm uppercase tracking-[0.16em] text-zinc-500 hover:text-white border-b border-white/[0.04] transition-colors';

  const mobileBuy = homeOnVideo
    ? 'block mt-4 w-full text-center py-4 bg-white text-neutral-900 font-heading text-sm font-semibold uppercase tracking-[0.2em] hover:bg-[#c5ebe2] transition-colors'
    : isHome
      ? 'block mt-4 w-full text-center py-4 bg-[#1c1b19] text-[#f5f3ef] font-heading text-sm font-semibold uppercase tracking-[0.2em] hover:bg-[#2c5f54] transition-colors'
      : 'block mt-4 w-full text-center py-4 bg-white text-neutral-900 font-heading text-sm font-semibold uppercase tracking-[0.2em] hover:bg-zinc-200 transition-colors';

  return (
    <nav data-testid="navbar" className={`fixed top-9 left-0 right-0 z-50 transition-all duration-500 ${navBar}`}>
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link
            to="/"
            className="flex items-center gap-2.5 group"
            data-testid="nav-logo"
            aria-label="SprintFit — Home"
          >
            <div
              className={`h-12 w-12 md:h-14 md:w-14 shrink-0 skew-x-[-10deg] rounded-md overflow-hidden flex items-center justify-center p-0.5 ${logoMark}`}
              aria-hidden
            >
              <img
                src={SITE_IMAGES.sprintFitMark}
                alt=""
                className="h-full w-full max-h-[92%] max-w-[92%] object-contain object-center skew-x-[10deg]"
              />
            </div>
            <span className="flex flex-col leading-tight">
              <span className={`font-heading font-bold text-xl md:text-2xl uppercase tracking-[0.14em] ${wordmark}`}>
                Sprint<span className={cubeAccent}>Fit</span>
              </span>
              <span className={`font-mono text-[9px] md:text-[10px] uppercase tracking-[0.22em] transition-colors ${subLabel}`}>
                Pro-grade recovery · India
              </span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map((link) =>
              link.to ? (
                <Link key={link.label} to={link.to} className={linkClass}>
                  {link.label}
                </Link>
              ) : (
                <a key={link.label} href={link.href} onClick={(e) => handleHashClick(e, link.href)} className={linkClass}>
                  {link.label}
                </a>
              )
            )}
            <a href={SPRINTFIT_COLLECTIONS_URL} target="_blank" rel="noopener noreferrer" className={exploreClass}>
              Explore all
            </a>
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            <a href={SPRINTFIT_COLLECTIONS_URL} target="_blank" rel="noopener noreferrer" className={mobileExplore}>
              Explore all
            </a>
            <a
              href={SPRINTFIT_COLLECTIONS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-buy-btn"
              className={buyBtn}
            >
              Shop SprintFit
            </a>
            <button
              onClick={() => setIsMobileOpen(!isMobileOpen)}
              className={`md:hidden p-2 transition-colors ${menuIcon}`}
              aria-label="Toggle menu"
            >
              {isMobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {isMobileOpen && (
        <div className={mobilePanel}>
          <div className="px-6 py-6 space-y-1">
            {NAV_LINKS.map((link) =>
              link.to ? (
                <Link key={link.label} to={link.to} className={mobileLink} onClick={() => setIsMobileOpen(false)}>
                  {link.label}
                </Link>
              ) : (
                <a key={link.label} href={link.href} onClick={(e) => handleHashClick(e, link.href)} className={mobileLink}>
                  {link.label}
                </a>
              )
            )}
            <a
              href={SPRINTFIT_COLLECTIONS_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setIsMobileOpen(false)}
              className={mobileLink}
            >
              Explore all
            </a>
            <a
              href={SPRINTFIT_COLLECTIONS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={`${mobileBuy} text-center`}
            >
              Shop SprintFit
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};
